import { memo, useCallback } from 'react'
import React from 'react'

import { Box, Button, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { IoArrowBack } from 'react-icons/io5'
import { useNavigate } from 'react-router-dom'

type Props = {
  text?: string
  onClick?: () => void
}

/** Retour navigateur, ou retour vers un calcul lié (reprise des résultats) lorsque `text` + `onClick` sont fournis. */
const BackButton = ({ text, onClick }: Props) => {
  const navigate = useNavigate()
  const linkedReturn = Boolean(text && onClick)

  const handleClick = useCallback(() => {
    if (onClick) {
      onClick()
    } else {
      navigate(-1)
    }
  }, [navigate, onClick])

  if (linkedReturn) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 0.75,
          maxWidth: '100%'
        }}>
        <Button
          type="button"
          variant="outlined"
          color="primary"
          size="small"
          onClick={handleClick}
          startIcon={<IoArrowBack size={18} aria-hidden />}
          aria-label={`${text} — les résultats de ce calcul seront repris dans l'écran précédent.`}
          sx={theme => ({
            textTransform: 'none',
            borderRadius: '10px',
            px: 1.75,
            py: 0.55,
            fontWeight: 700,
            fontSize: '0.8125rem',
            letterSpacing: '-0.01em',
            lineHeight: 1.3,
            borderWidth: 1.5,
            boxShadow: 'none',
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            '&:hover': {
              borderWidth: 1.5,
              boxShadow: 'none',
              bgcolor: alpha(theme.palette.primary.main, 0.1)
            },
            '& .MuiButton-startIcon': { ml: -0.25, mr: 0.75 }
          })}>
          {text}
        </Button>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            maxWidth: 400,
            lineHeight: 1.5,
            pl: 0.125,
            fontSize: '0.75rem'
          }}>
          Les résultats affichés ici seront appliqués au calcul d&apos;origine.
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
      <Button
        type="button"
        variant="outlined"
        color="primary"
        size="small"
        onClick={handleClick}
        startIcon={<IoArrowBack size={18} aria-hidden />}
        aria-label="Retour à l'écran précédent"
        sx={theme => ({
          textTransform: 'none',
          borderRadius: '10px',
          px: 1.75,
          py: 0.55,
          fontWeight: 700,
          fontSize: '0.8125rem',
          borderWidth: 1.5,
          boxShadow: 'none',
          bgcolor: alpha(theme.palette.primary.main, 0.02),
          '&:hover': {
            borderWidth: 1.5,
            boxShadow: 'none',
            bgcolor: alpha(theme.palette.primary.main, 0.08)
          }
        })}>
        Retour
      </Button>
    </Box>
  )
}

export default memo(BackButton)
